// D6 — THE COMMAND CENTER, AS LAW.
//
// Her order: **opening the dashboard shows THE BUSINESS, never a member list.** This file is what
// the landing view, the date machine and every ranged tab agree on — imported by BOTH the server
// (which computes the numbers) and the dashboard (which renders them), so a tile and the query
// behind it can never disagree about what "30 days" meant.
//
// Imports ONLY `./itTickets.js` and `./usagePage.js`, **each of which imports nothing** — so
// `scripts/d6-command-center.mjs` still loads and RUNS this with no browser, no database and no
// network. (ITEM 8 added the second one: the Review flags TILE and the Lala review VIEW must count
// and list the same gates, so there is ONE roster and the tile imports it rather than keeping a
// copy. A copy is exactly how those two came to have zero rows in common — see the health strip.)
//
// ITEM 2 (2026-08-10) — **HER ZONE, AND THERE IS ONE OF IT.** `PACIFIC` and `pacificDayKey` are
// IMPORTED rather than written a second time here: the ticket sheet already groups her days on that
// exact law and a dashboard that answered "what day is it" differently from her sheet would be two
// clocks in one house (R28/R29). `server/daily-morning.js`'s `HOUSE_ZONE` was the same zone under a
// THIRD name and now derives from this one, so the round ends with a single declaration.
import { PACIFIC, pacificDayKey } from "./itTickets.js";
// ITEM 8 — the Review flags tile counts what the Lala review queue lists, off that queue's OWN
// roster. Re-exported so the STORE builds its SQL from the same declaration the view renders from
// and the two can never be two answers (R28/R29/R31).
import { REVIEW_GATES } from "./usagePage.js";
// M1 — **THE ONE DECLARATION OF WHERE AN ALERT OPENS**, imported rather than typed twice. It
// imports NOTHING itself, so this file's own reach law (a `.mjs` gate loads and RUNS it with no
// browser, no database and no network) is untouched.
import { alertDoor, doorQuery } from "./alertDoors.js";

export { PACIFIC, pacificDayKey, REVIEW_GATES };

// ---------------------------------------------------------------------------
// ITEM 2 — **A PACIFIC MIDNIGHT IS NOT UTC ARITHMETIC, AND THAT IS THE WHOLE ROUND.**
//
// Every window on this dashboard was built out of `Date.UTC(...)`, so **from 17:00 Pacific onward her
// "Today" tile showed TOMORROW's UTC day** — her entire evening of business landed on a day she could
// not see, every day, and the 7/10/30/90/year windows were bounded on UTC midnights for the same
// reason. This is not a display bug: the bounds are what the SQL is given, so the numbers themselves
// were for the wrong day.
//
// Resolving "midnight in her zone" to an instant needs the zone's offset AT THAT INSTANT, and the
// offset depends on the instant — which is circular, so it is solved in two passes. Format the
// candidate in the zone, read those parts back AS IF they were UTC, and the difference is the offset;
// then re-derive with that offset and check it still holds. **The second pass is not belt-and-braces:
// on the two DST days a year the first guess lands on the other side of the change**, and a
// dashboard that is an hour wrong twice a year is one nobody can reconcile against Stripe.
const dayMs = 86_400_000;

export function zoneOffsetMs(atMs, zone = PACIFIC) {
  const at = new Date(Number(atMs) || 0);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
  }).formatToParts(at);
  const get = (t) => Number(parts.find((p) => p.type === t)?.value);
  const asIfUtc = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second"));
  return asIfUtc - Math.floor(at.getTime() / 1000) * 1000;
}

// The instant midnight begins in her zone on a `YYYY-MM-DD` day key.
export function startOfDayMs(dayKey, zone = PACIFIC) {
  const [y, m, d] = String(dayKey).split("-").map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return NaN;
  const wall = Date.UTC(y, m - 1, d, 0, 0, 0);
  const first = wall - zoneOffsetMs(wall, zone);
  const second = wall - zoneOffsetMs(first, zone);
  return second;
}

// Day-key arithmetic done on the KEYS rather than on epoch ms, because adding 86,400,000 to an
// instant crosses a DST boundary into the wrong day twice a year — the exact error this round is
// about, reintroduced one line lower.
export function dayKeyPlus(dayKey, delta, zone = PACIFIC) {
  const noon = startOfDayMs(dayKey, zone) + (dayMs / 2);
  return pacificDayKey(noon + (Number(delta) || 0) * dayMs, zone);
}

// ---------------------------------------------------------------------------
// D6-2 — THE DATE MACHINE. **ONE RANGE CONTROL GOVERNING THE WHOLE DASHBOARD.**
//
// Her list, exactly: Today, 7 days, 10 days, 30 days, 90 days, year, and CUSTOM start/end. The range
// is ONE piece of state read by every tile, chart and table, so two tabs cannot disagree about what
// this month means — which is the bug this shape exists to make impossible rather than to fix later.
//
// C7 (2026-08-15) — **HER ROSTER IS REPLACED AT ITS DECLARATION, NOT OVERRIDDEN BELOW IT**
// (DL-2/DT-2b/R181). Her order: *"ONE dropdown on mobile AND desktop replacing the stretched button
// row. Options exactly: Today, Yesterday, 7 days, 30 days, This month, Last month, This year,
// Custom."* So `10d`, `90d` and the rolling-365 `year` LEAVE, and four periods arrive that the old
// shape could not express at all.
//
// >>> **AND THAT IS THE ROUND: THREE OF HER FOUR NEW PERIODS ARE NOT "THE LAST N DAYS".** <<< Every
// window this file has ever resolved ended at the end of TODAY and ran back a declared number of
// days. **Yesterday ends YESTERDAY. Last month ends on the last day of last month. This month and
// this year are CALENDAR windows whose length is a different number every time they are asked.** A
// roster that answered all four with a `days` count would be wrong about three of them — silently,
// on the one control every tile on this dashboard reads.
//
// So a range declares its SPAN and the boundaries are DERIVED from it (`windowKeys` below):
//   • `days`  — N whole days ending `endsDaysAgo` days back. Today, Yesterday, 7 days, 30 days.
//   • `month` — the calendar month, this one or the previous one.
//   • `year`  — the calendar year to date.
//   • `custom`— her own two dates.
// **`days` is a RESULT for everything but the rolling windows**, because `priorRange` reads it: a
// declared 30 on "Last month" is wrong in seven months of the year and moves her comparison chip
// with nothing on screen to say so.
// ---------------------------------------------------------------------------
export const RANGES = [
  { key: "today", label: "Today", span: "days", days: 1, endsDaysAgo: 0 },
  { key: "yesterday", label: "Yesterday", span: "days", days: 1, endsDaysAgo: 1 },
  { key: "7d", label: "7 days", span: "days", days: 7, endsDaysAgo: 0 },
  { key: "30d", label: "30 days", span: "days", days: 30, endsDaysAgo: 0 },
  // The two month windows are told apart by their own SPAN rather than by a `monthsAgo` NUMBER: a
  // number is a thing that can quietly be 2, and a "two months ago" nobody offers is a window with
  // no control behind it (R165's shape one roster over).
  { key: "mtd", label: "This month", span: "month", days: null },
  { key: "lastmonth", label: "Last month", span: "prev-month", days: null },
  { key: "ytd", label: "This year", span: "year", days: null },
  { key: "custom", label: "Custom", span: "custom", days: null },
];
export const RANGE_KEYS = RANGES.map((r) => r.key);
export const DEFAULT_RANGE = "today";
export const rangeValid = (key) => RANGE_KEYS.includes(String(key || ""));

// A range resolved to real boundaries. `nowMs` is passed IN rather than read from the clock, so this
// is pure and a fixture can pin a day without pinning the machine's date.
//
// DAY BOUNDARIES, NOT ROLLING HOURS: "7 days" that starts at 14:23 last Tuesday makes every
// day-over-day comparison a lie by a fraction of a day. Ranges start at midnight.
// C7 — THE TWO DAY KEYS A SPAN RESOLVES TO, AND NOTHING ELSE. Every window in this file is a pair of
// day keys; the instants, the length and the label are all derived from that pair ONE time below, so
// a new span cannot arrive carrying its own slightly-different notion of where a day ends.
//
// **THE MONTH BOUNDARY IS STRING ARITHMETIC ON A DAY KEY AND NEVER A SUBTRACTION IN DAYS.**
// "Last month" as `todayKey - 30 days` is wrong in every month but one, and it is wrong QUIETLY —
// the tile draws, the numbers add up, and they are a month that does not exist. `dayKeyPlus` is what
// crosses the boundary, because it does the arithmetic on the KEYS and survives the two DST days a
// year that a millisecond subtraction does not.
// HOW MANY WHOLE DAYS A PAIR OF INSTANTS SPANS. **IT IS A RESULT AND NEVER A DECLARATION**, because
// three of her eight periods are a different length every time they are asked and `priorRange` reads
// this number to place her comparison chip.
//
// The division is ROUNDED rather than floored and that is load-bearing: a span containing a DST
// change is 23 or 25 hours long on one of its days, so the quotient is a fraction of a day off and a
// floor would report a 30-day month as 29. The error is at most an hour per crossing against a
// 24-hour unit, so rounding is exact for every span this dashboard can produce.
function spanDays(startMs, endMs) {
  return Math.max(1, Math.round((endMs + 1 - startMs) / dayMs));
}

function windowKeys(span, todayKey) {
  if (span === "month" || span === "prev-month") {
    const firstOfThis = `${todayKey.slice(0, 7)}-01`;
    if (span === "month") return { fromKey: firstOfThis, toKey: todayKey };
    // The last day of last month IS the day before the first of this one — derived rather than
    // looked up, so February, a leap February and a 31-day month all answer correctly with no table.
    const lastOfPrev = dayKeyPlus(firstOfThis, -1);
    return { fromKey: `${lastOfPrev.slice(0, 7)}-01`, toKey: lastOfPrev };
  }
  if (span === "year") return { fromKey: `${todayKey.slice(0, 4)}-01-01`, toKey: todayKey };
  return null;
}

export function resolveRange({ key = DEFAULT_RANGE, from = "", to = "", nowMs = 0 } = {}) {
  const todayKey = pacificDayKey(Number(nowMs) || 0);

  if (key === "custom") {
    const fromKey = String(from).slice(0, 10);
    const toKey = String(to).slice(0, 10);
    const start = startOfDayMs(fromKey);
    const end = startOfDayMs(dayKeyPlus(toKey, 1)) - 1;
    if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) {
      // An unusable custom range falls back to Today rather than to nothing: a dashboard that goes
      // blank because two dates were typed backwards reads as broken.
      return resolveRange({ key: DEFAULT_RANGE, nowMs });
    }
    return { key: "custom", label: `${fromKey} to ${toKey}`, startMs: start, endMs: end, days: spanDays(start, end), fromKey, toKey };
  }

  const spec = RANGES.find((r) => r.key === key) || RANGES[0];
  const anchored = windowKeys(spec.span, todayKey);
  // A ROLLING window ends `endsDaysAgo` whole days back — which is 0 for every one of them but
  // Yesterday, and Yesterday is the whole reason this is a field rather than a constant.
  const toKey = anchored ? anchored.toKey : dayKeyPlus(todayKey, -(Number(spec.endsDaysAgo) || 0));
  const fromKey = anchored ? anchored.fromKey : dayKeyPlus(toKey, -((Number(spec.days) || 1) - 1));
  const startMs = startOfDayMs(fromKey);
  // The window ENDS the last millisecond before the day AFTER its last day begins in her zone,
  // derived from the day key rather than by adding a day's worth of milliseconds — the DST-safe
  // form, and the one that lets a window end on a day that is not today.
  const endMs = startOfDayMs(dayKeyPlus(toKey, 1)) - 1;
  // `fromKey`/`toKey` RIDE ON THE RANGE, and that is not a convenience. `lala_turns` is keyed by a
  // `local_day` DATE column, and `$1::date` on a timestamptz is cast using the POSTGRES SERVER'S
  // timezone — a second clock hiding inside a query that looks bounded by the range. The store is
  // handed her day keys directly so that cast never happens (the pre-measure's clause (d)).
  return { key: spec.key, label: spec.label, startMs, endMs, days: spanDays(startMs, endMs), fromKey, toKey };
}

// THE PRIOR EQUAL PERIOD, for her "comparisons vs the prior equal period where cheap". Same length,
// ending the instant the current one begins, so the two never overlap by a day.
export function priorRange(range) {
  // ITEM 2 — **THE PRIOR PERIOD IS WHOLE PACIFIC DAYS TOO, AND IT CARRIES HER DAY KEYS.**
  //
  // Two things were wrong with subtracting a span in milliseconds. The obvious one: a period
  // containing a DST change is 23 or 25 hours on one of its days, so the arithmetic slid the prior
  // window an hour off her midnights and the comparison chip measured a slightly different question
  // from the tile above it. The one that would have taken the dashboard DOWN: `tilesFor` is called
  // with this object (`tilesFor(priorRange(range))`), and since the range now hands the store
  // `fromKey`/`toKey` for the `local_day` query, a prior range without them passes `undefined` as a
  // bound. A comparison figure is not worth a 500 on her Overview.
  const days = Number(range?.days) || 1;
  const toKey = dayKeyPlus(range?.fromKey || pacificDayKey(Number(range?.startMs) || 0), -1);
  const fromKey = dayKeyPlus(toKey, -(days - 1));
  return {
    startMs: startOfDayMs(fromKey),
    endMs: startOfDayMs(dayKeyPlus(toKey, 1)) - 1,
    days,
    fromKey,
    toKey,
  };
}

// A change between two numbers, for the comparison chips. Null when there is nothing to compare
// against — a "+100%" against a zero baseline is a number that means nothing.
export function delta(current, prior) {
  const a = Number(current) || 0;
  const b = Number(prior) || 0;
  if (!b) return { pct: null, direction: a > 0 ? "up" : "flat", text: "" };
  const pct = Math.round(((a - b) / b) * 100);
  return { pct, direction: pct > 0 ? "up" : pct < 0 ? "down" : "flat", text: `${pct > 0 ? "+" : ""}${pct}%` };
}

// ---------------------------------------------------------------------------
// D6-1 — THE LANDING VIEW'S TILES. Her list, in her order, and **every number is a door**.
//
// `to` names where a tap lands. A tile with no destination is not allowed here: her ruling says
// every number is tappable into its detail, so a tile that cannot say where it goes is a tile that
// has not been finished.
// ---------------------------------------------------------------------------
export const MONEY_TILES = new Set(["revenue", "vendorCost", "net"]);
export const TODAY_TILES = [
  { key: "signups", label: "Signups", to: "members" },
  // **ITEM 10 — PAID CUSTOMERS, FIRST-CLASS, BESIDE SIGNUPS. Her strengthening ruling, in her own
  // position.** For the selected Pacific period: the count of UNIQUE PEOPLE who completed a real
  // Stripe charge — new subscriptions, upgrades and one-time purchases alike, **deduped per person**
  // — read off the same reconciled charge walk as the revenue tile, so the two can never disagree
  // about which charges were in the window. It opens FINANCIALS, where the list of who paid and what
  // for is drawn: her clause is *"tapping the tile opens the list of who paid and for what"*, and
  // that list belongs beside the lanes it adds up to rather than on a seventeenth view of its own.
  { key: "paidCustomers", label: "Paid customers", to: "financials" },
  // ITEM A — **MEMBERS TOTAL, WHICH IS ONE OF THE THREE NUMBERS SHE NAMED FOR THIS SCREEN AND WAS
  // THE ONE THAT WAS NOT ON IT.** "Active members" answers a different question — who did something
  // in the window — and a dashboard that shows only that cannot answer "how many people are here".
  // Counted AS OF THE RANGE END rather than as a running total ignoring the picker, so the prior
  // period's figure beside it is the same question one period earlier and the delta is real growth.
  { key: "membersTotal", label: "Members total", to: "members" },
  { key: "activeMembers", label: "Active members", to: "members" },
  { key: "generations", label: "Generations run", to: "analytics" },
  { key: "lalaTurns", label: "Lala turns", to: "analytics" },
  // **REVENUE IS REAL STRIPE CASH AND CARRIES NO ESTIMATE TAG** (her order, 2026-08-08): gross minus
  // refunds for the selected period, read from Stripe itself and reconciling to her Stripe dashboard.
  // The modelled run-rate it replaced showed $450.13 on a day Stripe took $4,507.76.
  { key: "revenue", label: "Revenue", to: "financials" },
  // **VENDOR COST IS STILL MODELLED AND STILL SAYS SO.** It is generations x 4c + Lala turns x 3c —
  // per-unit figures measured in earlier rounds, not invoices. Nothing bills this house per
  // generation, so there is no cash reading to replace it with; the tag is the honest answer.
  { key: "vendorCost", label: "Vendor cost", to: "financials", estimate: true },
  // **NET IS MIXED, AND THAT IS WHY IT KEEPS THE TAG**: real cash minus a modelled cost. The estimate
  // rides on the half that is estimated rather than being dropped because the other half got real.
  { key: "net", label: "Net", to: "financials", estimate: true },
];
export const TILE_KEYS = TODAY_TILES.map((t) => t.key);

// ---------------------------------------------------------------------------
// ITEM 8 / A2 — **THE ALERT BOXES ARE DOORS ONTO THE THING THEY COUNT, AND TWO OF THEM WERE NOT.**
//
// Her complaint is that the Overview alert boxes are DEAD. Measured against the live log before a
// line was written (R25/R180), that is three separate defects and only one of them is cosmetic.
//
//   1. **`errors` HAS NEVER BEEN ABLE TO FIRE.** Its predicate was `outcome ILIKE '%error%'`, and
//      **the substring "error" appears in no `outcome` value this house has ever written** — 0 rows
//      all time, against 524,235 gate trips in thirty days. The error-shaped outcomes are sitting
//      right there in the same column under different words: **`route-threw` 196, `handler-threw`
//      37, `engine-failed` 12.** So the tile whose whole job is to say something broke has read a
//      confident **0** for as long as it has existed, on a house where 245 handlers really threw in
//      a month. That is CE-4's headline clause — *dashboards can never read green over failing
//      members* — arriving through a predicate that cannot match.
//   2. **`reviewFlags` COUNTED ONE SET AND OPENED ONTO ANOTHER, WITH ZERO ROWS IN COMMON.** It
//      counted `gate_log WHERE delivered = false` — **48 rows, all of them `seal-generate` (32) and
//      `seal-truth-gate` (16)** — and the door it opens is the Lala review queue, which lists
//      `REVIEW_GATES` and holds **33,743** rows over the same window. **Neither seal gate is on that
//      roster**, so she tapped a tile saying 48 and landed on a queue containing not one of them.
//      Her clause 1, word for word: *a real door onto the thing it counts.*
//   3. **`gateTrips` IS 86% BOOKKEEPING.** 524,235 trips in thirty days, of which **449,607 are
//      `gate = 'push'`** — rows carrying a null outcome and a null `delivered`, written by the push
//      rail as it works. The view it opens says *"Every time something was stopped before it reached
//      a member"*; a push row is not a stop. **HER ORDER: THE TILE LEAVES THE HEALTH ROW** (the junk
//      sweep). The `gates` view is untouched under Advanced — the TILE leaves, nothing is deleted.
//
// **THE REPAIR THAT KEEPS IT FIXED IS THE CROSSING, NOT THE PREDICATE.** `reviewFlags` now reads
// `REVIEW_GATES` — **the review view's OWN roster, imported** — so the number and the list are one
// declaration and cannot drift apart again (R28/R29/R31). A tile with its own hand-typed copy of a
// door's roster is exactly how these two came to disagree, and it disagreed silently, because a
// number with the wrong rows behind it renders perfectly.
//
// **AND `dormant` IS DELETED AT ITS DECLARATION** (DL-2/DT-2b/R181) rather than left standing.
// `pushSends` carried `dormant: true` and rendered the caption *"when push ships"* — while that
// number measured **45,567 pushed rows and 401 live subscriptions.** A caption claiming a shipped
// feature has not shipped is her clause 2 in its purest form: an alert she cannot act on. The tile
// counts something real and keeps its door; the claim that it does not goes.
// ---------------------------------------------------------------------------

// **THE ERROR VOCABULARY IS WORDS, NOT THREE EXACT STRINGS, AND THAT IS DELIBERATE (R17/R160).** A
// hand-typed roster of `route-threw`/`handler-threw`/`engine-failed` is a receipt that rots the
// first time a fourth thing throws — silently, in the direction that reads as a healthy month
// (R8-4). This house names a fault by what happened to it, so a fourth outcome spelled `sweep-threw`
// joins by EXISTING. Checked against every outcome value the live log holds: these three words match
// exactly the three that are faults and none of the seventeen that are not (`no-corroboration`,
// `composed-fallback`, `cooldown-blocked` and the rest all pass through untouched).
export const HEALTH_ERROR_WORDS = ["threw", "failed", "error"];
export function outcomeIsError(outcome) {
  const s = String(outcome == null ? "" : outcome).toLowerCase();
  if (!s) return false;
  return HEALTH_ERROR_WORDS.some((w) => s.includes(w));
}
// The SQL is DERIVED from the same words rather than written a second time, so the tile's number and
// the law a gate RUNS can never be two answers (R28/R29). Every word is a literal in this file and
// none of it is member-typed, so there is nothing here a caller could inject.
//
// **IT IS BUILT BY CONCATENATION RATHER THAN BY A NESTED TEMPLATE, AND THAT IS AN INSTRUMENT
// DECISION RATHER THAN A STYLE ONE.** R104 resolves a mutation anchor only when it is a static
// literal, and an anchor quoting a line that carries `${` is one that census cannot read — six of
// this round's own mutations came back unresolvable on the first gate run. A line a censor cannot
// read is a line nobody is guarding (R8-4), so the source is written to be readable.
export function healthErrorOutcomeSql(col = "outcome") {
  const parts = HEALTH_ERROR_WORDS.map((w) => String(col) + " ILIKE '%" + w + "%'");
  return "(" + parts.join(" OR ") + ")";
}

// ITEM 8 — **WHAT THE TILE TELLS HER BEFORE SHE TAPS IT.** One law rather than a template inside the
// markup, for the same reason: a gate RUNS this and reads the answer back, where a regex over JSX
// can only ever ask whether two strings sit beside each other (L12/CRYSTAL's finding).
export function healthTileLabel(tile) {
  const t = tile || {};
  return String(t.label || "") + " — " + String(t.counts || "") + " Open " + String(t.to || "") + ".";
}

// THE HEALTH STRIP — **and every tile on it declares the DOOR it opens AND what it COUNTS.**
//
// `to` is where a tap lands; `counts` is the one-line answer to *what is this number* in the words
// she would use. A tile that cannot say both is refused by `healthTileViolations` below, because a
// number with nowhere to go and a number nobody can describe are the two shapes her *"the alert
// boxes are DEAD"* complaint is made of.
// >>> M1 — **`to` IS DERIVED FROM `alertDoors.js` HERE TOO, AND THE REASON IS THE PATROL LINE.**
// <<< A2 repaired these four tiles and the patrol row BESIDE them kept its own answer: the
// `generationFailures` TILE opened `alerts` while the `failures` PATROL ROW — same condition, her
// same words, one line apart on the same screen — opened `tickets`. Two surfaces naming one thing
// may not hold two destinations (R28/R29), and the only way that is a property rather than a
// coincidence is for neither of them to type it. `doorFor` reads the roster once at module load,
// so every existing reader of `t.to` is untouched.
const doorFor = (key) => alertDoor(key)?.view || "";
export const HEALTH_TILES = [
  // Repaired predicate, unchanged door: the gate log lists every gate/outcome pair, so a thrown
  // handler is already visible there the moment the tile can count one.
  { key: "errors", label: "Errors", to: doorFor("errors"), counts: "Handlers and engines that threw or failed." },
  { key: "pushSends", label: "Push sends", to: doorFor("pushSends"), counts: "Notifications this house handed to a phone." },
  // ITEM 8 — the tile reads the door's own roster now. `REVIEW_GATES` is re-exported below.
  { key: "reviewFlags", label: "Review flags", to: doorFor("reviewFlags"), counts: "Turns the gates flagged for your eye." },
  // SURFACE 3, CLAUSE 1 — **THE TILE LEAVES ANALYTICS.** Her ruling: *failures are not statistics,
  // they are flags.* Analytics is a place she READS; the alert surface is a place she ACTS, and the
  // rows behind this number now live there with their member, their cause and their remedy. The
  // tile itself stays, because it is the door — and a door that opened onto a statistics table was
  // A2's exact complaint about every box on this page.
  { key: "generationFailures", label: "Readings that failed", to: doorFor("generationFailures"), counts: "Readings a member asked for and did not get." },
];

/**
 * ITEM 8 — **HER TWO CLAUSES AS ARITHMETIC OVER THE ROSTER, NEVER AS A COMMENT.**
 *
 * Every health tile names a door that exists, and says what it counts. **A tile that declares itself
 * DORMANT is refused outright**: that flag is how a number she cannot act on came to render for
 * months under a caption that was false, and her clause is that such a box does not render at all.
 *
 * `viewExists` is a PARAMETER so a gate drives this over rosters this repo does not contain
 * (R129/R135) rather than measuring its own alphabet.
 */
export function healthTileViolations(tiles = HEALTH_TILES, viewExists = viewValid) {
  const bad = [];
  for (const t of tiles || []) {
    const key = String(t?.key || "");
    if (!key) { bad.push("a health tile with no key"); continue; }
    if (!t.to || !viewExists(t.to)) bad.push(key + " points at " + (t.to || "nothing") + ", which is not a view");
    if (!t.counts || typeof t.counts !== "string" || !t.counts.trim()) bad.push(key + " does not say what it counts");
    if ("dormant" in (t || {})) bad.push(key + " is marked dormant, and an alert she cannot act on does not render");
  }
  return bad;
}

// ---------------------------------------------------------------------------
// L17 — **THE RECOVERY LINE ON FINANCIALS: failed, in grace, recovered, frozen.**
//
// Her clause names four numbers, and **TWO OF THEM ARE EVENTS AND TWO ARE STATES — which is R18 on a
// row of figures.** *How many cards failed* and *how many members recovered* happened inside a window
// and are RANGED; *how many are in grace* and *how many are frozen* are facts about right now and are
// NOT. `membershipCensus` one section down has exactly this shape and states its own reason in as
// many words — *"a fact about right now; scoping it to the period would answer a different
// question"* — so the split is the house's existing ruling rather than a new one.
//
// **AND THE ROW HAS TO SAY WHICH IS WHICH, OR SHE READS FOUR NUMBERS AS ONE PERIOD.** Four figures
// under one heading carrying a date is a claim about the date; two of these are true of today
// whatever the picker says. The `ranged` flag rides the ROSTER rather than the render, so a fifth
// number joins on a declared side or not at all.
//
// **THEY ARE COUNTS OF PEOPLE, NOT OF EVENTS, AND THAT IS THE ONE ARITHMETIC DECISION IN THE ROW.**
// Stripe fires `invoice.payment_failed` once PER RETRY ATTEMPT for days, so a row count would report
// five failures for one member's one card — the same trap L14's `payment_failed_at IS NULL` was
// written to close, arriving one layer up in a number she reads. Both event counts are DISTINCT
// MEMBERS, so the two are the same unit and can be read side by side.
//
// **NO RATE IS COMPUTED, DELIBERATELY** (R19: no number her order did not ask for). A member can
// fail on the last day of one window and recover on the first day of the next, so recovered/failed
// is legitimately over 100% some months — a percentage here would be a figure that looks wrong when
// the house is working.
export const RECOVERY_ROWS = [
  { key: "failed", label: "Cards that failed", ranged: true },
  { key: "recovered", label: "Recovered", ranged: true },
  { key: "inGrace", label: "In grace right now", ranged: false },
  { key: "frozen", label: "Frozen right now", ranged: false },
];
export const RECOVERY_KEYS = RECOVERY_ROWS.map((r) => r.key);

/**
 * **THE SENTENCE THAT KEEPS THE ROW HONEST ABOUT WHOSE FIGURES THESE ARE.** `lanesOk`'s precedent
 * two functions down: every other number on this tab is reconciled to Stripe to the cent, and these
 * four are the HOUSE's own reading of its own rows. Presented silently beside the lane table they
 * would inherit that claim, and a figure she believes came from Stripe is the one wrong belief this
 * tab can give her.
 */
export const RECOVERY_NOTE =
  "These four come from this house's own records rather than from Stripe, so they are not part of the "
  + "reconciliation above. The first two count people inside the period; the last two are how things "
  + "stand right now, whatever period is selected.";

// ---------------------------------------------------------------------------
// DL-3.3 — THE MORNING STRIP. **A SILENT MORNING MUST BE IMPOSSIBLE TO MISS** (her acceptance).
//
// DL-1 made a skip a ROW, as loud as a send, precisely so this could exist: "the job ran and decided
// not to send" and "the job did not run" are two different mornings, and a build that counts only
// sends collapses them into one reassuring number. So the three facts are kept apart here.
//
//   1. **NO ROWS AT ALL IS NOT A CLEAN MORNING, IT IS A SILENCE**, and it is the only state on this
//      strip that means something is broken rather than something was decided. It gets the sentence.
//   2. **A ROW STILL `claimed` IS A MORNING THAT STARTED AND NEVER FINISHED** — the process died
//      between the claim and the settle. It is not a skip: nobody decided anything, and because the
//      claim is what makes a send single, that member gets nothing tomorrow either until the day
//      rolls. Counted and named on its own.
//   3. **THE REASONS ARE RANKED, BECAUSE A TOTAL IS NOT A PATTERN.** Twelve skips and twelve skips
//      all saying `below-tier` are different mornings and want different acts from her.
//
// **THE DAY IS PART OF THE ANSWER AND IS ALWAYS PRINTED.** The ledger is keyed by each member's own
// LOCAL day (DL-1a), so this is deliberately NOT summed across the range picker: a range would mix
// zones and produce a number nobody can act on. One day, named on the strip.
export const MORNING_SILENT_NOTE = "No mornings recorded. The job did not run.";

export function morningStrip(health) {
  const h = health && typeof health === "object" ? health : null;
  const byStatus = (h && h.byStatus) || {};
  const byReason = (h && h.byReason) || {};
  const n = (v) => Number(v) || 0;
  const sent = n(byStatus.sent);
  const skipped = n(byStatus.skipped);
  const unfinished = n(byStatus.claimed);
  const total = n(h && h.total) || sent + skipped + unfinished;
  const reasons = Object.entries(byReason)
    .map(([reason, count]) => ({ reason, n: n(count) }))
    // Ranked, then by NAME, so two reasons with the same count do not swap places between refreshes
    // and read as movement.
    .sort((a, b) => b.n - a.n || a.reason.localeCompare(b.reason));
  const state = total === 0 ? "silent" : (skipped || unfinished) ? "mixed" : "clean";
  return { day: String((h && h.day) || ""), total, sent, skipped, unfinished, reasons, state };
}

// **THE ONLY STATE THAT SAYS ANYTHING IS THE BROKEN ONE.** A strip that apologises on a clean
// morning is a strip she stops reading (R75's `none` argument, one instrument over).
export function morningNote(strip) {
  return strip && strip.state === "silent" ? MORNING_SILENT_NOTE : "";
}

// Money is rendered from cents, and it says when it is TEST MODE — her order: "test-mode until
// launch, wired for live". A revenue number that does not say which it is is a number she could act
// on wrongly.
export function money(cents) {
  const n = Number(cents) || 0;
  const sign = n < 0 ? "-" : "";
  return `${sign}$${(Math.abs(n) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
export const TEST_MODE_NOTE = "test mode";
export function moneyLabel(cents, { testMode = false } = {}) {
  return testMode ? `${money(cents)} (${TEST_MODE_NOTE})` : money(cents);
}

// ---------------------------------------------------------------------------
// **ITEM 10 — THE CONVERSION LINE, HER WORDS: "X signups, Y paid, Z%".**
//
// It lives HERE, in the law both the server and the dashboard import, because it is rendered on the
// Overview AND on the Financials tab and two copies of one sentence is how the two screens start
// disagreeing about the same period (R28/R29).
//
// **THREE STATES, NOT TWO (R18), AND THE MIDDLE ONE IS WHY THIS IS A FUNCTION AND NOT A TEMPLATE
// STRING.** `paid === null` is a Stripe read that FAILED — the percentage is unknowable and saying
// so is the only honest line; `signups === 0` is a real answer with no denominator, where a
// percentage would be a division by zero rendered as `Infinity%` or, worse, `0%`, which reads as
// nobody converting. Only when there is a real denominator is there a percentage.
// ---------------------------------------------------------------------------
export const CONVERSION_UNREAD = "Paid customers could not be read, so the conversion is unknown.";

export function conversionLine({ signups, paid } = {}) {
  const s = Number(signups) || 0;
  if (paid === null || paid === undefined) return { signups: s, paid: null, pct: null, text: CONVERSION_UNREAD };
  const p = Number(paid) || 0;
  const n = (v) => v.toLocaleString("en-US");
  const word = (v) => `${n(v)} signup${v === 1 ? "" : "s"}`;
  if (!s) return { signups: 0, paid: p, pct: null, text: `${word(0)}, ${n(p)} paid.` };
  const pct = Math.round((p / s) * 100);
  return { signups: s, paid: p, pct, text: `${word(s)}, ${n(p)} paid, ${pct}%.` };
}

// AN ESTIMATE SAYS SO. The vendor cost is modelled from usage, not invoiced, and net inherits that.
// Her honest-gap law applied to her own instruments: a number that is a guess is labelled a guess.
export const ESTIMATE_NOTE = "estimate";

// ---------------------------------------------------------------------------
// ITEM A / D6-3 — **SIX SECTIONS AND NOTHING ELSE, AND A VIEW BELONGS TO EXACTLY ONE OF THEM.**
//
// HER ORDER: *corporate, easy to use, easy to understand, not complicated.* Six top-level sections —
// OVERVIEW, MEMBERS, FINANCIALS, MODERATION, IT TICKETS, GRANTS — with the deep diagnostic views
// under ONE collapsed **ADVANCED** area at the bottom. **NOTHING IS DELETED UNDERNEATH:** every
// surface that existed still exists and still loads; what changed is where it is reached from.
//
// **WHY THIS IS TWO ROSTERS AND NOT ONE.** A VIEW is a surface that renders; a SECTION is a door on
// her menu. D6-3 had one list doing both jobs, and the moment three surfaces (`triage`, `catalogs`,
// `review`) needed to exist without earning a top-level door they were typed as LITERALS at the
// render site in App.tsx — so `TAB_KEYS`, the roster `MEMBER_DOOR_TABS`, `SEARCHABLE_TABS`,
// `RANGED_TABS` and four scripts are all keyed on, **has never described what the dashboard actually
// shows.** `MEMBER_DOOR_TABS` names `triage`, which `TAB_KEYS` does not contain: a set membership
// test against a roster that cannot contain the member. Both rosters live here now and `TABS` is
// DERIVED, so there is no second answer to what this dashboard can render (R28/R29).
//
// **THE BICONDITIONAL IS THE LAW WITH TEETH:** the union of every section's `views` is exactly
// `VIEW_KEYS`, and no view is claimed twice. A view in no section is a surface with no door — which
// is precisely how `catalogs` came to be reachable only through a hand-typed literal; a view in two
// sections is two answers to where she acts on it.
// ---------------------------------------------------------------------------

// A VIEW is one surface. `memberContent` and `acts` carry HER NO-DEAD-READ-ONLY-LISTS clause as
// data: *"if she can see it she can act on it or it does not render"*. A view that draws a member's
// words and declares no act is refused by the law below rather than by somebody noticing.
export const VIEWS = [
  { key: "overview", label: "Overview", note: "The day, and what needs you." },
  // SURFACE 3, CLAUSES 1 AND 2 / A2's FIRST ACT — **THE STANDING ALERTS, AND EVERY ONE OF THEM IS A
  // DOOR.** Her complaint about the Overview boxes is that they are DEAD: a number with nowhere to
  // go. This view is where a raised condition opens into the member, the reading and the cause, with
  // the remedy on the same card — so `memberContent` with real acts on it, which is her
  // no-dead-read-only-lists clause holding this surface by existing rather than by exception.
  { key: "alerts", label: "Alerts", note: "What is still going wrong, and what you have already seen.", memberContent: true, acts: ["clear", "open", "heal"] },
  { key: "members", label: "Members", note: "Humans only. The one place a member is listed.", memberContent: true, acts: ["open"] },
  { key: "financials", label: "Financials", note: "Revenue, purchases, refunds, vendor cost, margin." },
  // ITEM A — THE MERGED QUEUE. Everything held or flagged, the content in front of her, the decision
  // on the same card.
  { key: "moderation", label: "Needs you", note: "Everything held or flagged, with the words in front of you.", memberContent: true, acts: ["approve", "decline", "hide", "delete", "warn"] },
  { key: "living", label: "The room seat", note: "Reports, photos, your messages, and what you have cleared.", memberContent: true, acts: ["approve", "hide", "delete", "warn", "pin", "allow", "send"] },
  // VEIL-1 — her group oversight, BESIDE Moderation because that is where her order puts it and
  // because it is the same job: terms enforcement, bullying complaints and technical remedy. It
  // declares its acts like every other member-content view, so her no-dead-read-only-lists clause
  // covers it by existing rather than by exception.
  { key: "veil", label: "Group oversight", note: "Every group on the site. Opening one is written down.", memberContent: true, acts: ["ticket", "flag", "warn"] },
  // ITEM 9 — her IT sheet. Not RANGED and not SEARCHABLE by design: it is grouped into her own
  // Pacific days, and a range picker over a day-grouped sheet would be two answers to one question.
  { key: "tickets", label: "Today's list", note: "What is broken, by day, ready to hand to CC.", memberContent: true, acts: ["clear", "copy"] },
  { key: "triage", label: "Drafts", note: "Written for you. Nothing is an order until you approve it.", memberContent: true, acts: ["approve", "reject", "open"] },
  { key: "messages", label: "Wrote in", note: "What members sent you, and your answer.", memberContent: true, acts: ["open", "reply"] },
  { key: "grants", label: "Grants", note: "Gifts and refunds." },
  // >>> REV-1's APPROVAL LEDGER. HER THREE-TAP DESK. <<< Her order: *"the approval ledger to her
  // dashboard per the amended spec: three-tap desk, first names default, provenance behind the
  // toggle, DM privacy law"* -- and **her approvals are the gate before any display renders**, so
  // this surface is what the home carousel, the membership page's three review slots and the desktop
  // review rail are all waiting on. It IS `memberContent`: every row is a member's own words.
  { key: "reviews", label: "Reviews", note: "What members said. Nothing shows until you approve it.", memberContent: true, acts: ["approve", "trim", "skip"] },
  // B5 — HER MOSES WORD MAP. CC proposes a word -> tag mapping with her book's OWN sentence beside
  // it; nothing binds until she taps. Same shape as Reviews one row up and for the same reason: a
  // list she can only READ is the dead-read-only-list her ITEM A order forbids, so it declares its
  // acts and every row has a decision on it.
  { key: "wordmap", label: "Seal words", note: "What members ask for, and which seal it should reach. Nothing binds until you approve it.", memberContent: true, acts: ["approve", "edit", "skip"] },
  // THE BROADCAST TOOL (her queue item 4, 2026-08-11). **A SEVENTH TOP-LEVEL DOOR, BY HER OWN WORD**
  // — her order opens *"a new owner-only dashboard section, Broadcasts"*, which is the one thing that
  // may add to ITEM A's six (that law is hers and this sentence is hers). It is NOT `memberContent`:
  // the history it lists is what SHE wrote, so her no-dead-read-only-lists clause is satisfied by the
  // acts below rather than by exception.
  { key: "broadcasts", label: "Broadcasts", note: "Write to the house. Push, email, or both.", acts: ["compose", "preview", "send"] },
  // TEACH-1 — HER TEACHING ROOM. It is NOT `memberContent`: every row is HER OWN word, taught by her
  // and authored to her address, so ITEM A's no-dead-read-only-lists clause is satisfied by the
  // acts below rather than by exception. It carries a SECOND LOCK on top of the owner session
  // (D3-3's passcode, her clause 1), which `veil` is the precedent for.
  { key: "teaching", label: "Teaching", note: "Teach her a correction. It is live the moment you save it.", acts: ["teach", "edit", "retire", "test"] },
  // Q-DASH (2), her order: the TRUTH tab. One surface with its own three parts (Analytics, Approvals, Financials).
  { key: "truth", label: "Truth", note: "Truth by 12th House: who came, who applied, and the money." },
  // ---- ADVANCED. The machine language is allowed to live here and nowhere above it.
  { key: "analytics", label: "Analytics", note: "Rooms, generations, growth, Lala volume." },
  { key: "delivery", label: "Mornings and delivery", note: "Whether the morning ran and whether it reached her phone." },
  // ITEM 7c, HER ORDER OF 2026-10-01 (afternoon): "this is what I want removed from the dashboard" —
  // the old stack (what is waiting, last night's check, the /talk door, the Truth watchman). 6b had
  // folded it at the foot of Overview; her word now is that NONE of it is on Overview, and that it
  // stays reachable here, under Advanced. Moved, never deleted: the same four components, the same
  // payload, every line still a door.
  { key: "checks", label: "Waiting and last night's checks", note: "What used to sit at the top of Overview: what is waiting on you, last night's check, and the two watchmen." },
  { key: "gates", label: "Gate log", note: "Every time something was stopped before it reached a member." },
  { key: "probes", label: "Probe evidence", note: "Members who kept pushing past a warm no.", memberContent: true, acts: ["rule", "open"] },
  { key: "review", label: "Lala review", note: "Flagged turns. Behind your support passcode.", memberContent: true, acts: ["open"] },
  // A4 — **THE JUNK SWEEP: `catalogs` IS GONE FROM HER VIEW, AND THE GATE LOG STAYS WITH ITS
  // REASON.** Her clause is *"GATE LOGS and CATALOGS GONE from her view (data stays queryable)"*.
  //
  // **THE CATALOGS VIEW IS DELETED AT ITS DECLARATION** (DL-2/DT-2b/R181), not merely unrouted — the
  // surface, its component, its state, its render arm and `/api/owner/catalogs` all go together,
  // because R202 reads a route no client calls as UNREACHED and R203 reads its every field as dead.
  // A view left on this roster with no arm behind it is ITEM A's own biconditional broken; a view
  // taken off the roster with its arm still there is a surface with no door. There is no half of
  // this that holds. **Her *(data stays queryable)* is met to the letter: not one row, file or
  // catalog reader is touched** — the seals, the herbs, the recipes and the deck are read by Lala,
  // the Book of Roots, the Spellbook and the owner's purpose-review route exactly as before, and
  // `handleOwnerPurposeReview` — the one that WRITES her rulings — is untouched.
  //
  // **AND THE GATE LOG STAYS, DECLARED RATHER THAN SILENTLY KEPT (DECISIONS A4-1).** ITEM 8 shipped
  // three days after A4 was written and its whole finding was that the Errors tile had never been
  // able to fire (a confident 0 over 245 real thrown handlers); its own ruling is *"the TILE leaves,
  // the `gates` VIEW is untouched under Advanced"*, and `healthTileViolations` REFUSES a tile with
  // no door. Deleting `gates` now would take away the door to the thing ITEM 8 has just made
  // visible for the first time. A4's own parenthesis is about her VIEW, and ITEM A already took it
  // off the six and put it behind a collapsed Advanced.
];
export const VIEW_KEYS = VIEWS.map((v) => v.key);
export const viewValid = (key) => VIEW_KEYS.includes(String(key || ""));
export const viewSpec = (key) => VIEWS.find((v) => v.key === String(key || "")) || null;

// HER SIX, IN HER ORDER, AND THEN ADVANCED. Overview is first because D6-1 says the dashboard opens
// on the business; Advanced is LAST and collapsed because her sentence puts it "at the bottom of the
// menu".
export const SECTIONS = [
  // >>> **HER ORDER, 2026-10-01, IN HER WORDS: "overview, members, financials, IT tickets, grants,
  // broadcast, teaching, and analytics all at the top. Everything else can go under advanced because
  // I don't even really use any of it."** <<< This supersedes ITEM A's six (which had Moderation as
  // a top door and Analytics under Advanced). NOTHING IS DELETED: Moderation's four surfaces (the
  // merged queue, the room seat, group oversight and reviews) moved under Advanced whole, and
  // `goToView` still opens Advanced by itself when a tap lands in there.
  //
  // Alerts stays under Overview (DECISIONS SURFACE3-1, unchanged).
  { key: "overview", label: "Overview", views: ["overview", "alerts"] },
  { key: "members", label: "Members", views: ["members"] },
  { key: "financials", label: "Financials", views: ["financials"] },
  { key: "tickets", label: "IT Tickets", note: "Support: the list, the drafts, and what members wrote in.", views: ["tickets", "triage", "messages"] },
  { key: "grants", label: "Grants", views: ["grants"] },
  // Her word is "broadcast"; the label is hers. The view inside keeps its own name.
  { key: "broadcasts", label: "Broadcast", views: ["broadcasts"] },
  { key: "teaching", label: "Teaching", views: ["teaching"] },
  { key: "analytics", label: "Analytics", views: ["analytics"] },
  // Q-DASH (2): her TRUTH tab, beside her other top doors.
  { key: "truth", label: "Truth", views: ["truth"] },
  {
    key: "advanced", label: "Advanced", advanced: true,
    note: "Everything else. Nothing in here is lost; it is just out of the way.",
    // Her 2026-10-01 order moved Moderation's four surfaces in here, first, as one block.
    // A4: `catalogs` came OFF this list with its view.
    views: ["moderation", "living", "veil", "reviews", "checks", "delivery", "gates", "probes", "review", "wordmap"],
  },
];
export const SECTION_KEYS = SECTIONS.map((s) => s.key);

/**
 * **A SECTION HOLDING ONE VIEW BORROWS THAT VIEW'S NOTE (R31).** Four of her six are one surface
 * each, and the first draft typed the label and the note into BOTH rosters — `grants` was the same
 * characters twice, which is two answers to what a door is called and is what R68's shared-literal
 * ceiling caught in the round that wrote it. A section that spans several views says its own thing,
 * because no single view can speak for the group.
 */
export function sectionNote(section) {
  const s = SECTIONS.find((x) => x.key === String(section || ""));
  if (!s) return "";
  if (s.note) return s.note;
  const first = VIEWS.find((v) => v.key === s.views[0]);
  return first ? first.note : "";
}
// R165: there is deliberately no `sectionValid`. Nothing validates a SECTION key — every tap routes
// through `goToView`, which takes a VIEW and derives the section, so a section is never the thing a
// caller has in hand. A guard nobody calls is a capability with no error state.
export const DEFAULT_SECTION = "overview";

/** The section a view is reached from. Null for a view nobody rostered — which the law refuses. */
export function sectionFor(view) {
  const v = String(view || "");
  return SECTIONS.find((s) => s.views.includes(v)) || null;
}
/** The views a section holds, as their full specs and in the section's own order. */
export function viewsOf(section) {
  const s = SECTIONS.find((x) => x.key === String(section || ""));
  return s ? s.views.map(viewSpec).filter(Boolean) : [];
}
/** What a section opens on: its FIRST view, never a second declaration of the same fact. */
export function defaultViewOf(section) {
  const s = SECTIONS.find((x) => x.key === String(section || ""));
  return s ? s.views[0] : "";
}

/**
 * WHERE A TAP LANDS. **Every number and every line on this dashboard names a VIEW, and a view has to
 * be OPENED — which means opening the section that holds it, including the collapsed one.** A tile
 * that set the view without opening its section would leave her looking at a menu that disagrees
 * with the page under it; a line pointing into Advanced would silently do nothing.
 *
 * An unrostered destination falls back to the default section rather than to a blank page, because a
 * dashboard that goes empty because somebody typed a key wrong reads as broken.
 */
export function goToView(view) {
  const s = sectionFor(view);
  if (!s) return { section: DEFAULT_SECTION, view: defaultViewOf(DEFAULT_SECTION), openAdvanced: false };
  return { section: s.key, view: String(view), openAdvanced: Boolean(s.advanced) };
}

// D6's four sets, still keyed on view keys — and now against a roster that can actually contain
// them. `triage` has been in this set since A1 and in no roster until today.
export const TABS = VIEWS;
export const TAB_KEYS = VIEW_KEYS;
export const tabValid = viewValid;

// The views that carry their own search (D6-4). Named here so a view cannot quietly ship without one.
export const SEARCHABLE_TABS = new Set(["members", "messages", "probes"]);

// The views a RANGE applies to. The moderation and grant seats are not time series and must not grow
// a range control that does nothing.
export const RANGED_TABS = new Set(["overview", "analytics", "financials", "truth"]);

// ---------------------------------------------------------------------------
// ITEM A — **PLAIN LANGUAGE EVERYWHERE SHE LOOKS, AND ADVANCED IS WHERE THE MACHINE MAY SPEAK.**
//
// Her clause, in her own examples: *no `line-refused`, no DT-2, no beacon jargon in a default view —
// "Did not reach her phone, tap for detail"*. Three detectors, each calibrated on one of the three
// shapes she named, because a roster of banned words alone is a list somebody has to keep adding to:
//
//   1. **A MACHINE KEY** — a lowercase hyphenated compound. `line-refused`, `below-tier`,
//      `endpoint-gone`, `worker-not-adopted`, `drew-nothing`. Every skip reason, delivery cause and
//      chain state this system writes down is this shape, which is why the shape is the detector and
//      not a list of them. The FIXTURE drives this over those real rosters rather than trusting it.
//   2. **A RULING CODE** — `DT-2`, `LR1-15`, `R141`, `N12-3`. Her own second example.
//   3. **AN UNDERSCORE** — `gate_log`, `hidden_reason`. English prose has none.
//
// Plus a small roster for single words that are machinery and look like English (`beacon`, `payload`).
// **A single-word key is NOT jargon on its own**: `unknown`, `delivered` and `waiting` are keys AND
// ordinary words, and refusing them would refuse her own approved labels. That limit is declared
// rather than hidden (R17/R160) — what this law is about is a machine KEY reaching her screen.
//
// **IT IS ASKED OF THE SIX AND NOT OF ADVANCED**, which is her sentence exactly: the deep views are
// where the machine's own names belong, and a law that forbade them there would forbid the gate log
// from naming a gate.
// ---------------------------------------------------------------------------
export const MACHINE_KEY_RE = /\b[a-z]+(?:-[a-z]+)+\b/;
// The hyphen between the letters and the first number is OPTIONAL, and that is not a detail: this
// house writes both `R141`/`N12-3` and `DT-2`/`LR1-15`, and **her own worked example of the defect is
// `DT-2`** — so a reader that demanded the digits touch the letters missed the exact string she
// pointed at. Caught by this fixture's own DRIVEN clause on its first run.
export const RULING_CODE_RE = /\b[A-Z]{1,4}-?\d+(?:[-.]\d+[a-z]?)?\b/;
export const JARGON_WORDS = [
  "beacon", "payload", "endpoint", "webhook", "tsvector", "regex", "enum", "boolean",
  "null", "undefined", "sql", "cron", "worker", "vapid", "tombstone", "predicate",
];

/** What is wrong with a sentence she is meant to read. Empty means nothing is. */
export function plainLanguageViolations(text) {
  const s = String(text || "");
  const bad = [];
  if (s.includes("_")) bad.push("underscore");
  const key = s.match(MACHINE_KEY_RE);
  if (key) bad.push(`machine key: ${key[0]}`);
  const code = s.match(RULING_CODE_RE);
  if (code) bad.push(`ruling code: ${code[0]}`);
  for (const w of JARGON_WORDS) {
    if (new RegExp(`\\b${w}\\b`, "i").test(s)) bad.push(`jargon: ${w}`);
  }
  return bad;
}
// R165 — **`isPlainLanguage` IS DELETED RATHER THAN LEFT EXPORTED WITH NO CALLER.**
// `plainLanguageViolations` is what every caller actually wants: WHICH way a sentence is wrong is
// what somebody has to act on, and a boolean throws that away (R17 — a total is not a pattern). A
// second spelling of one question is the shape this whole file is about.

/**
 * HER NO-DEAD-READ-ONLY-LISTS CLAUSE, AS ARITHMETIC OVER THE ROSTER.
 *
 * *"No dead read-only lists: if she can see it she can act on it or it does not render."* A view
 * that draws member content and offers nothing to do with it is refused. **Asked of the six and not
 * of Advanced** — her own sentence puts the diagnostic views there, and a diagnostic that could not
 * be looked at without an action bar would not be a diagnostic.
 */
export function deadListViolations() {
  const bad = [];
  for (const s of SECTIONS) {
    if (s.advanced) continue;
    for (const v of viewsOf(s.key)) {
      if (v.memberContent && !(Array.isArray(v.acts) && v.acts.length)) bad.push(`${s.key}/${v.key}`);
    }
  }
  return bad;
}

/** Every label and note she reads outside Advanced, with what is wrong with it. */
export function jargonViolations() {
  const bad = [];
  const say = (where, text) => {
    for (const why of plainLanguageViolations(text)) bad.push(`${where}: ${why}`);
  };
  for (const s of SECTIONS) {
    if (s.advanced) { say(`section ${s.key} label`, s.label); continue; }
    say(`section ${s.key} label`, s.label);
    say(`section ${s.key} note`, s.note);
    for (const v of viewsOf(s.key)) {
      say(`view ${v.key} label`, v.label);
      say(`view ${v.key} note`, v.note);
    }
  }
  for (const n of NEEDS_YOU) {
    say(`needs ${n.key} one`, n.one);
    say(`needs ${n.key} many`, n.many);
  }
  say("needs clear", NEEDS_YOU_CLEAR);
  return bad;
}

// ---------------------------------------------------------------------------
// ITEM A — **WHAT NEEDS HER, AND EVERY LINE CLICKS THROUGH TO WHERE SHE ACTS ON IT.**
//
// Her Overview clause names three: held items awaiting review, tickets open, money issues in her
// pile. Each `to` is a VIEW, held by the law below against the roster — a line that names a
// destination nobody rosters is a sentence telling her to go somewhere that does not exist.
//
// **THREE STATES, NOT TWO (R18), AND THE MIDDLE ONE IS THE WHOLE REASON THIS IS A FUNCTION.**
// `counts === null` is a payload that HAS NOT ARRIVED and it holds still; a payload that arrived
// with three zeros is an ANSWER and says so; a payload with work in it lists the work. Collapsing
// the first two puts **"Nothing needs you right now."** on the screen of an owner whose dashboard
// could not read the database — which is the one wrong thing to tell her, and it is R65's finding on
// this exact page in another spelling.
// ---------------------------------------------------------------------------
// >>> M1 — **`to` IS DERIVED FROM `alertDoors.js` AND IS NO LONGER TYPED HERE.** <<< The `money`
// line carried `to: "grants"` while its count reads `triage_drafts`, so she landed on 400 gifts and
// refunds and not one of her seven money questions. A destination typed beside a count that reads a
// different table is exactly the drift one declaration removes (R28/R29) — see that module's header
// for the measurement. The KEY is what a line is; where it opens is the door's answer.
export const NEEDS_YOU = [
  { key: "held", one: "1 item is waiting for your review", many: "{n} items are waiting for your review" },
  { key: "tickets", one: "1 ticket is open", many: "{n} tickets are open" },
  { key: "money", one: "1 money question is in your pile", many: "{n} money questions are in your pile" },
  // >>> **DESK-LAW-1 (2026-09-04) — HER 48-HOUR PROMISE, ON THE LIST SHE READS FIRST.** <<<
  //
  // Her standing ruling inside item 10: *"no member waits past 48 hours without a real answer, the
  // backlog count rides every morning scoreboard."* The count EXISTED — `deskBacklog` is how c188
  // and c162 were found — and it was readable only by the overnight hunter, which files into
  // `RUNBOOK.md`. **A law whose only reader is a file she does not open in the morning is a law
  // nothing asks** (R38/R165). So it joins the list she already reads by EXISTING (R11/R31) rather
  // than as a fourth box on the Overview she has to learn to look at.
  //
  // **TWO LINES, BECAUSE THEY ARE TWO MORNINGS (R18).** Eleven waiting and none late is a workload;
  // three waiting and two late is this house breaking its own promise, and a single summed number
  // would hide which one she is looking at. The late line is drawn from the same people as the
  // waiting line, so it READS as a subset and is worded as one — and **THAT WORDING IS WHY THE
  // ORDER IS FIXED RATHER THAN INCIDENTAL**: `needsYouLines` walks this roster in declaration order,
  // so a late line drawn ABOVE its own total says *"2 of them"* with nothing for *them* to refer to.
  // The waiting count is declared first and the breach second, and the lock drives that order.
  //
  // **THEY GO AFTER HER THREE AND NOT ABOVE THEM.** A broken promise arguably outranks an open
  // ticket, but the clause under this roster is *"her three lines are the three she named"*, and
  // reordering her own named list on a machine's judgement is the conservative choice's opposite
  // (DECISIONS DESK-LAW-1). Appended, both lines are on the screen; her order is still hers.
  { key: "desk", one: "1 member is waiting for a real answer", many: "{n} members are waiting for a real answer" },
  { key: "deskLate", one: "1 of them has been waiting longer than 48 hours", many: "{n} of them have been waiting longer than 48 hours" },
];
export const NEEDS_YOU_KEYS = NEEDS_YOU.map((n) => n.key);
export const NEEDS_YOU_CLEAR = "Nothing needs you right now.";
// R18/R65: a list that could not be READ says so. It is a SEPARATE sentence rather than the clear
// one, because "nothing needs you" over a database that refused the read is the one wrong thing to
// tell an owner — she stops looking.
export const NEEDS_YOU_UNREAD = "Your list could not be read just now.";

export function needsYouLines(counts) {
  if (!counts || typeof counts !== "object") return null;
  const lines = [];
  for (const n of NEEDS_YOU) {
    const value = Math.max(0, Math.trunc(Number(counts[n.key]) || 0));
    if (!value) continue;
    // M1 — the DOOR answers where this opens and what is behind it. A line whose alert has no door
    // is not drawn at all (her clause 4), which cannot happen for these three and is held as a law
    // rather than assumed: `doorViolations` refuses a drawn alert with no door either way.
    const door = alertDoor(n.key);
    if (!door) continue;
    lines.push({
      key: n.key, to: door.view, filter: door.filter, query: doorQuery(door), items: door.items, n: value,
      text: value === 1 ? n.one : n.many.replace("{n}", value.toLocaleString("en-US")),
    });
  }
  return lines;
}

/** The three shapes this roster must never take, asked as one question the fixture can drive. */
export function rosterViolations() {
  const bad = [];
  const claimed = new Map();
  for (const s of SECTIONS) {
    for (const v of s.views) {
      if (!viewValid(v)) bad.push(`section ${s.key} names a view that does not exist: ${v}`);
      if (claimed.has(v)) bad.push(`view ${v} is claimed by ${claimed.get(v)} and ${s.key}`);
      claimed.set(v, s.key);
    }
  }
  for (const v of VIEW_KEYS) if (!claimed.has(v)) bad.push(`view ${v} has no section`);
  // M1 — a needs-you line's destination is its DOOR's, so this asks the door rather than a field
  // that no longer exists. An alert with no door at all is refused here as well as by
  // `doorViolations`, because these three are her actionable list and a line she cannot open is the
  // whole complaint.
  for (const n of NEEDS_YOU) {
    const door = alertDoor(n.key);
    if (!door) bad.push(`the ${n.key} line has no door`);
    else if (!viewValid(door.view)) bad.push(`the ${n.key} line points at ${door.view}, which is not a view`);
  }
  for (const t of [...TODAY_TILES, ...HEALTH_TILES]) if (!viewValid(t.to)) bad.push(`the ${t.key} tile points at ${t.to}, which is not a view`);
  return bad;
}

// ---------------------------------------------------------------------------
// D6-5 — REAL TIME, HONESTLY SCOPED.
//
// The Today view polls; ranged views compute on demand and cache briefly; **everything reads STORED
// DATA and makes ZERO vendor calls.** And where a number is not live it SAYS SO with the time it was
// taken — her honest-gap law, applied to the instruments she runs the business on.
// ---------------------------------------------------------------------------
export const TODAY_POLL_MS = 60_000;
export const RANGED_CACHE_MS = 120_000;

// A SNAPSHOT NEVER SAYS "LIVE". This label is only ever used by a ranged view, which is a picture of
// a period that is not moving — and "Taken live · 30 days" is a sentence that claims something the
// view is not doing. A freshly computed snapshot is "just now"; only the Today view says live, and it
// says it in its own words because it is the only one actually polling.
export function freshnessLabel(takenMs, nowMs) {
  const age = Math.max(0, Number(nowMs) - Number(takenMs));
  if (age < 15_000) return "just now";
  const mins = Math.floor(age / 60_000);
  if (mins < 1) return `${Math.floor(age / 1000)}s ago`;
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ago`;
}

// Is this view claiming to be live? Today polls, so it is; a ranged view is a snapshot and says the
// time it was taken instead.
export function isLiveView(rangeKey) {
  return rangeKey === "today";
}

// ---------------------------------------------------------------------------
// D6-3 — CSV EXPORT, AND IT IS THE SAME EXPORT WHATEVER THE RANGE IS.
//
// Her Analytics and Financials tabs are "ranged, CSV-exportable". The export is built FROM THE
// PAYLOAD ALREADY ON SCREEN rather than from a second server query, so the file she opens in a
// spreadsheet is by construction the table she was looking at — a separate export query is how a
// CSV starts disagreeing with the page above it.
//
// The quoting is the whole reason this is a shared law rather than a template string in a component:
// a room key with a comma in it, or an owner note with a quote mark, silently shifts every column to
// its right and nothing about the file looks wrong.
export function csvCell(value) {
  const s = value === null || value === undefined ? "" : String(value);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
export function toCsv(columns, rows) {
  const keys = columns.map((c) => (typeof c === "string" ? c : c.key));
  const heads = columns.map((c) => (typeof c === "string" ? c : c.label || c.key));
  // CRLF, because that is what a spreadsheet on her machine expects, and a BOM-less LF file opens
  // with every row on one line in Excel.
  return [heads.map(csvCell).join(","), ...rows.map((r) => keys.map((k) => csvCell(r[k])).join(","))].join("\r\n");
}

// The file NAMES ITS RANGE. A folder of "analytics.csv" downloads is a folder of files she cannot
// tell apart a week later, and a ranged export whose name does not carry the range is exactly that.
export function csvFilename(what, range) {
  // ITEM 2: the stamp is HER day, not UTC's. `toISOString()` names the day in London, so an export
  // she took at 6pm Pacific was filed under tomorrow's date — and a file named for a day it does not
  // contain is worse than an unnamed one when she opens it a month later.
  const stamp = (ms) => pacificDayKey(Number(ms) || 0);
  return `12thhouse-${what}-${range?.fromKey || stamp(range?.startMs)}-to-${range?.toKey || stamp(range?.endMs)}.csv`;
}

// ---------------------------------------------------------------------------
// D6-4 — EVERY MEMBER NAME ANYWHERE IS A DOOR.
//
// Named here, in the law, so a tab that lists members cannot quietly ship without the door: the
// fixture reads this set and then reads the RENDERED tab. Her sentence is "tap it and the member
// modal opens right there, without leaving the tab" — so the modal belongs to the dashboard, not to
// the members table, and this set is the list of tabs it must open from.
// `living` is in this list and it is worth saying why: the Living Room's identity law means the only
// thing ANY surface renders is the room first-name, and that is untouched here — the door rides the
// opaque `ref` the room already carries for reporting and blocking. What she sees is still @Name;
// what a tap does is open that member's file in HER seat, which is the one place it may.
export const MEMBER_DOOR_TABS = new Set(["members", "messages", "probes", "triage", "living"]);

// ---------------------------------------------------------------------------
// **ADVANCED IN HER LANGUAGE (her order, 2026-08-08). EVERY BOX GETS ONE PLAIN PHRASE.**
//
// Her words: *Mornings and Delivery are the daily-guidance push system. Rename and caption in owner
// language ... Every box on every Advanced screen gets a one-phrase plain caption, no jargon words
// anywhere.* These screens were built for whoever is diagnosing a silent morning, and those are the
// right words for that job and the wrong words on a screen she opens to find out whether her members
// heard from her.
//
// **THE CAPTIONS ARE A ROSTER RATHER THAN JSX, SO A BOX CANNOT SHIP WITHOUT ONE.** `jargonViolations`
// already refuses a machine key in anything she reads; this is the other half — a box with a number
// and no sentence is a number she has to interpret.
// ---------------------------------------------------------------------------
export const ADVANCED_TITLES = {
  mornings: "Daily guidance sent",
  delivery: "Reached phones",
  audit: "Yesterday's guidance, checked against phones",
};

export const ADVANCED_CAPTIONS = {
  // Daily guidance sent
  "mornings.sent": "Members who got their guidance today.",
  "mornings.skipped": "Members with nothing to send yet, usually no chart on file.",
  "mornings.unfinished": "Started and never finished. These members get nothing until tomorrow.",
  // Reached phones
  "delivery.sent": "Notifications this house sent out.",
  "delivery.received": "Arrived on the member's phone.",
  "delivery.displayed": "Actually appeared on her screen.",
  "delivery.undelivered": "Sent, and never arrived. Tap a name for the likely reason.",
  // Yesterday's guidance, checked against phones
  "audit.members": "Members the morning run covered.",
  "audit.sent": "Guidance messages sent that morning.",
  "audit.delivered": "Confirmed as arriving on a phone.",
};

/** The caption for a box, or "" — never a key, and never silence dressed as a caption. */
export const advancedCaption = (key) => ADVANCED_CAPTIONS[String(key || "")] || "";

/**
 * Every Advanced caption in her language, held as arithmetic rather than as a promise: a jargon word,
 * a machine key or a ruling code in any of them is RED, and so is a box with no caption at all.
 */
export function advancedCaptionViolations() {
  const bad = [];
  for (const [key, text] of Object.entries(ADVANCED_CAPTIONS)) {
    if (!text || text.trim().length < 12) bad.push(`${key}: no real caption`);
    for (const why of plainLanguageViolations(text)) bad.push(`${key}: ${why}`);
  }
  for (const [key, text] of Object.entries(ADVANCED_TITLES)) {
    for (const why of plainLanguageViolations(text)) bad.push(`title ${key}: ${why}`);
  }
  return bad;
}
